import { useCallback, useEffect, useRef, useState, type RefObject } from "react"
import useIsomorphicLayoutEffect from "../../helpers/useIsomorphicLayoutEffect"
import { createPortal } from "react-dom"

import { Box, ButtonBase, LinearProgress, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import Autoplay from "embla-carousel-autoplay"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"
import truncate from "lodash/truncate"

import { gsap } from "@ggs/ui/local/gsap"
import { IconButton, ResponsiveImage, SvgIcon, type FC, type ResponsiveImageType } from ".."

type HeroMediaProps = {
  media?: ResponsiveImageType[]
  video?: string
  containerRef?: RefObject<HTMLDivElement>
  title?: string
  delay?: boolean
  arrowsVisible?: boolean
  homePage?: boolean
  // Shift the position of the location tag
  shiftPosition?: boolean
}

const HeroMedia: FC<HeroMediaProps> = ({
  media = [],
  video,
  containerRef,
  title,
  delay = false,
  arrowsVisible = true,
  homePage = false,
  shiftPosition = false,
}) => {
  if (video) return <HeroVideoMedia src={video} />
  if (media && media?.length > 1)
    return (
      <HeroCarouselMedia
        items={media}
        containerRef={containerRef}
        delay={delay}
        arrowsVisible={arrowsVisible}
        homePage={homePage}
        shiftPosition={shiftPosition}
      />
    )
  return (
    <HeroImageMedia
      image={media?.[0]}
      title={title}
      delay={delay}
      homePage={homePage}
      shiftPosition={shiftPosition}
    />
  )
}
export default HeroMedia

/** A sub-component to render a video for the hero content */
type VideoProps = { src: string }

const HeroVideoMedia: FC<VideoProps> = ({ src }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)

  const playPause = useCallback(() => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play()
        setIsPlaying(true)
      } else {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
  }, [videoRef.current])

  return (
    <>
      <Box
        ref={videoRef}
        component="video"
        autoPlay
        playsInline
        muted
        loop
        position="absolute"
        width="100%"
        height="100%"
        sx={{ objectFit: "cover" }}
        zIndex={1}
      >
        <source src={src} type="video/mp4" />
      </Box>
      <Box
        position="absolute"
        width="100%"
        height="100%"
        sx={{
          background: (theme) =>
            `linear-gradient(180deg, ${alpha(theme.palette["neutral.900"].main, 0)} 0%, ${alpha(
              theme.palette["neutral.900"].main,
              0.4
            )} 100%)`,
          pointerEvents: "none",
        }}
        zIndex={2}
      />
      <IconButton
        icon={
          <SvgIcon
            variant={isPlaying ? "pause" : "play"}
            size={12}
            sx={{ fill: (theme) => theme.palette["neutral.100"].main }}
          />
        }
        aria-label="Play Pause button"
        onClick={playPause}
        sx={(theme) => ({
          "position": "absolute",
          "zIndex": 4,
          "right": theme.spacing(3),
          "bottom": theme.spacing(3),
          "backgroundColor": alpha(theme.palette["neutral.900"].main, 0.05),
          "border": `1px solid ${alpha(theme.palette["neutral.100"].main, 0.2)}`,
          "backdropFilter": "blur(4px)",

          "&:hover, &:focus-visible": {
            backgroundColor: alpha(theme.palette["neutral.900"].main, 0.4),
          },
        })}
      />
    </>
  )
}

/** A sub-component to render an image carousel for the hero content */
type CarouselProps = {
  items: ResponsiveImageType[]
  containerRef?: RefObject<HTMLDivElement>
  delay?: boolean
  arrowsVisible?: boolean
  homePage?: boolean
  shiftPosition?: boolean
}

const HeroCarouselMedia: FC<CarouselProps> = ({
  items,
  containerRef,
  delay = false,
  arrowsVisible,
  homePage = false,
  shiftPosition = false,
}) => {
  const mainRef = useRef<HTMLDivElement>(null)
  const cursorArrow = useRef<HTMLDivElement>(null)
  const [cursorActive, setCursorActive] = useState<"prev" | "next" | null>(null)
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 })
  const [location, setLocation] = useState(items[0]?.variants?.[0]?.title)
  const [scrollProgress, setScrollProgress] = useState(0)

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true },
    arrowsVisible
      ? []
      : [
        Autoplay({
          delay: 8000,
        }),
      ]
  )

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev()
      if (!emblaApi.canScrollPrev()) {
        setCursorActive(null)
      }
    }
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext()
      if (!emblaApi.canScrollNext()) {
        setCursorActive(null)
      }
    }
  }, [emblaApi])

  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (emblaApi) {
        const current = emblaApi.selectedScrollSnap()
        const location = items[current]?.variants?.[0]?.title
        setLocation(location)
      }
    },
    [emblaApi]
  )

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (emblaApi) {
        const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
        setScrollProgress(progress * 100)
      }
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
    emblaApi.on("scroll", onScroll)
  }, [emblaApi, onSelect])

  useIsomorphicLayoutEffect(() => {
    if (!mainRef.current) return

    const ctx = gsap.context(() => {
      const carousel = mainRef.current?.querySelector("[data-name='hero-carousel']")
      if (!carousel) return

      gsap.to(carousel, {
        y: () => carousel.clientHeight * 0.5,
        ease: "none",
        scrollTrigger: {
          start: delay ? "130px top" : "top top",
          end: "bottom top",
          scrub: true,
        },
      })
    }, mainRef)

    return () => ctx.revert()
  }, [mainRef])

  useIsomorphicLayoutEffect(() => {
    if (!cursorArrow.current || !mainRef.current) return

    const ctx = gsap.context(() => {
      const render = () => {
        gsap.to(cursorArrow.current, {
          duration: 0.3,
          x: () => cursorPos.x,
          y: () => cursorPos.y,
        })
      }
      requestAnimationFrame(render)
    }, mainRef)

    return () => ctx.revert()
  }, [cursorPos, mainRef, cursorArrow])

  const winBody = typeof window !== "undefined" ? window.document.body : null
  const carouselArrows = () => {
    if (winBody && arrowsVisible) {
      return createPortal(
        <>
          {emblaApi?.canScrollPrev() && (
            <ButtonBase
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                width: "50%",
                cursor: "none",
                zIndex: 3,
              }}
              onClick={scrollPrev}
              onMouseEnter={() => setCursorActive("prev")}
              onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setCursorActive(null)}
              tabIndex={-1}
              aria-label="Previous button"
            />
          )}
          {emblaApi?.canScrollNext() && (
            <ButtonBase
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: "50%",
                cursor: "none",
                zIndex: 3,
              }}
              onClick={scrollNext}
              onMouseEnter={() => setCursorActive("next")}
              onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setCursorActive(null)}
              tabIndex={-1}
              aria-label="Next button"
            />
          )}
        </>,
        containerRef?.current ?? winBody
      )
    }
    return null
  }

  return (
    <Box
      ref={mainRef}
      position="absolute"
      width="100%"
      height="100%"
      overflow="hidden"
      sx={{ bgcolor: (theme) => theme.palette["secondary.800"].main, borderRadius: 1.5 }}
    >
      <Box
        ref={cursorArrow}
        sx={{
          color: "white",
          position: "fixed",
          opacity: cursorActive === null ? 0 : 1,
          top: 0,
          left: 0,
          zIndex: 100,
          pointerEvents: "none",
          cursor: "none",
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          position="relative"
          width={70}
          height={70}
          bgcolor={(theme) => theme.palette["secondary.800"].main}
          boxShadow={(theme) => `2px 2px 8px ${alpha(theme.palette["neutral.900"].main, 0.08)}`}
          borderRadius={9999}
          sx={{ transform: "translate(-50%, -50%)" }}
        >
          <SvgIcon
            variant="arrow"
            sx={{
              fill: (theme) => theme.palette["neutral.700"].main,
              ...(cursorActive === "next" && { transform: "rotate(180deg)" }),
            }}
          />
        </Stack>
      </Box>

      <Box
        ref={emblaRef}
        data-name="hero-carousel"
        position="absolute"
        top="0"
        left="0"
        right="0"
        height="100%"
      >
        <Box display="grid" gridAutoFlow="column" gridAutoColumns="100%" height="100%">
          {items.map((image, i) => (
            <Box key={image?.variants?.[0]?.url || `hero-media-${i}`} position="relative">
              <ResponsiveImage
                image={image}
                position="absolute"
                width="100%"
                height="100%"
                sx={{ objectFit: "cover" }}
                zIndex={2}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "auto"}
              />
              <Box
                position="absolute"
                width="100%"
                height="100%"
                top={0}
                bottom={0}
                left={0}
                right={0}
                sx={{
                  background: (theme) =>
                    `linear-gradient(180deg, ${alpha(
                      theme.palette["neutral.900"].main,
                      0
                    )} 0%, ${alpha(theme.palette["neutral.900"].main, 0.55)} 100%)`,
                  pointerEvents: "none",
                }}
                zIndex={3}
              />
            </Box>
          ))}
        </Box>

        {carouselArrows()}
      </Box>
      <HeroLocation location={location} homePage={homePage} shiftPosition={shiftPosition} />
      <Box
        position="absolute"
        bottom={homePage ? { xs: 48, md: 88 } : { xs: 12, md: 29 }}
        left={{ xs: 12, md: "50%" }}
        sx={(theme) => ({
          transform: { md: "translateX(-50%)" },
          ...(shiftPosition && {
            left: { xs: "auto", md: "50%" },
            right: { xs: theme.spacing(2), md: "auto" },
            bottom: { xs: 24, md: 29 },
          }),
        })}
      >
        <LinearProgress
          variant="determinate"
          value={scrollProgress}
          aria-label="hero carousel progress bar"
          sx={{
            "width": "80px",
            "height": "2px",
            "&.MuiLinearProgress-root": {
              backgroundColor: (theme) => alpha(theme.palette["neutral.100"].main, 0.3),
            },

            "& .MuiLinearProgress-bar": {
              backgroundColor: (theme) => theme.palette["neutral.100"].main,
            },
            "zIndex": 0,
          }}
        />
      </Box>
    </Box>
  )
}

/** A sub-component to render an image for the hero content */
type ImageProps = {
  image?: ResponsiveImageType
  title?: string
  delay?: boolean
  homePage?: boolean
  shiftPosition?: boolean
}

const HeroImageMedia: FC<ImageProps> = ({
  image,
  title,
  delay = false,
  homePage = false,
  shiftPosition = false,
}) => {
  const mainRef = useRef<HTMLDivElement>(null)
  const location = image?.variants?.[0]?.title

  useIsomorphicLayoutEffect(() => {
    if (!mainRef.current) return

    const ctx = gsap.context(() => {
      const image = mainRef.current?.querySelector("img")
      if (!image) return

      gsap.to(image, {
        y: () => image.clientHeight * 0.5,
        ease: "none",
        scrollTrigger: {
          start: delay ? "130px top" : "top top",
          end: "bottom top",
          scrub: true,
        },
      })
    }, mainRef)
    return () => ctx.revert()
  }, [mainRef])

  return (
    <Box
      ref={mainRef}
      data-name="hero-image"
      position="absolute"
      top={0}
      right={0}
      left={0}
      height="100%"
      bgcolor={(theme) => theme.palette["secondary.800"].main}
      overflow="hidden"
      borderRadius={1.5}
    >
      {image && (
        <>
          <ResponsiveImage
            image={image}
            position="absolute"
            height="100%"
            sx={{ minHeight: 768, objectFit: "cover" }}
            zIndex={1}
            top={0}
            left={0}
            right={0}
            loading="eager"
            fetchPriority="high"
          />
          <Box
            position="absolute"
            width="100%"
            height="100%"
            sx={{
              background: (theme) =>
                `linear-gradient(180deg, ${alpha(theme.palette["neutral.900"].main, 0)} 0%, ${alpha(
                  theme.palette["neutral.900"].main,
                  0.4
                )} 100%)`,
              pointerEvents: "none",
            }}
            zIndex={2}
          />
          {location && (
            <HeroLocation location={location} homePage={homePage} shiftPosition={shiftPosition} />
          )}
          {!location && title && (
            <HeroLocation location={title} homePage={homePage} shiftPosition={shiftPosition} />
          )}
        </>
      )}
    </Box>
  )
}

/** A sub-component to render the location tag on the hero */
const HeroLocation: FC<{ location: string; homePage?: boolean; shiftPosition?: boolean }> = ({
  location,
  homePage = false,
  shiftPosition = false,
}) => {
  const trimmedLocation = truncate(location, { length: 50, omission: "..." })
  return (
    <Stack
      direction="row"
      columnGap={2}
      alignItems="center"
      position="absolute"
      zIndex={3}
      right={(theme) => theme.spacing(2)}
      bottom={(theme) =>
        homePage ? { xs: theme.spacing(10), md: theme.spacing(20) } : theme.spacing(3)
      }
      bgcolor={(theme) => alpha(theme.palette["neutral.900"].main, 0.05)}
      color={(theme) => theme.palette["neutral.100"].main}
      borderRadius={9999}
      px={3}
      py={2}
      sx={(theme) => ({
        backdropFilter: "blur(4px)",
        ...(shiftPosition && { left: theme.spacing(2), right: "auto" }),
      })}
    >
      <SvgIcon variant="location" size={{ xs: 8, md: 12 }} />
      <Typography variant="link.xs">{trimmedLocation}</Typography>
    </Stack>
  )
}
